import * as CL from '@design-system/component-library';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import {
  alvZeroMsg,
  catalogCorporateMessageLabelMsg,
  companysShareOfMonthlyFeeMsg,
  contractPeriodMsg,
  damageInsuranceMsg,
  defaultBillingAccountMsg,
  inUseMsg,
  monthMsg,
  nameOfCatalogMsg,
  notInUseMsg,
  pcsMsg,
  productTypeMsg,
  returnMsg,
  t,
} from '../../common/i18n';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../common/utils/billingAccountUtils.js';
import {
  getCorporateShareAsText,
  getDamageInsuranceDisplayableItems,
  productTypeDetails,
} from '../../common/utils/catalogUtils.js';
import { BillingAccountHeaderInfo } from '../AddOrSelectBillingAccountsV2/BillingAccountHeaderInfo.js';
import { BillingAccountInfo } from '../AddOrSelectBillingAccountsV2/BillingAccountInfo.js';
import { CatalogProductsSummary } from '../CatalogProductsSummary/CatalogProductSummary.js';
import { Table, TableDescription } from '../Table';
import { WizardActions } from '../WizardActions';
import { useSubmitCatalog } from './useSubmitCatalog';
import type { CatalogSummaryLoaderData } from '../../common/loaders';
import type { CLTableColumn } from '../../common/types/componentLibrary';
import type { Catalog } from '../../generated/api/catalog';
import type { ReadyFormValues } from '../CatalogConfiguration/CatalogConfigurationForm.js';

import './CatalogSummary.scss';

export const CatalogSummary = () => {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const { billingAccounts } = useLoaderData() as CatalogSummaryLoaderData;
  const params: ReadyFormValues & { catalog: Catalog } = state;
  const catalog = params.catalog;
  const baHeaders = (billingAccounts.searchResults ?? []).map(e => e.result);
  const selectedBillingAccountHeaders = baHeaders.filter(
    header => header.billingAccountId && catalog.selectedBillingAccountIds?.includes(header.billingAccountId)
  );
  const billingAccount = baHeaders.find(ba => ba.billingAccountId === params.catalog.billingAccountId);
  const { loading, submitCatalog } = useSubmitCatalog();

  const columns: CLTableColumn[] = [
    { key: 'billingAccountDisplayId', sortable: false, width: '10%' },
    { key: 'billingAccountName', sortable: false, width: '45%' },
    { key: 'payerName', sortable: false, width: '45%' },
  ];

  const rows = selectedBillingAccountHeaders.map(ba => {
    return {
      billingAccountDisplayId: <TableDescription title={ba.billingAccountDisplayId} />,
      billingAccountName: (
        <TableDescription description={ba.billingAccountExtensionName} title={ba.billingAccountName} />
      ),
      payerName: <TableDescription description={ba.payerNameExtension} title={ba.payerName} />,
    };
  });

  // If restricted list of billing account list is in use, show the default billing account in table
  if (selectedBillingAccountHeaders.length > 0) {
    rows.push({
      billingAccountDisplayId: <TableDescription title={billingAccount?.billingAccountDisplayId} />,
      billingAccountName: (
        <TableDescription
          description={billingAccount?.billingAccountExtensionName}
          title={billingAccount?.billingAccountName}
        />
      ),
      payerName: (
        <TableDescription description={billingAccount?.payerNameExtension} title={billingAccount?.payerName} />
      ),
    });
  }

  return (
    <div className="of-catalog-configuration__virtual-catalog-summary-overview">
      <CL.Description
        columns={1}
        items={[
          {
            title: t.M6TP(nameOfCatalogMsg),
            description: catalog.name,
          },
          {
            title: t.R6HP(productTypeMsg),
            description: catalog.productType && productTypeDetails()[catalog.productType].displayValue,
          },
          ...(catalog.contractPeriod
            ? [
                {
                  title: t.ULI0(contractPeriodMsg),
                  description: `${catalog.contractPeriod} ${t.XXVX(monthMsg)}`,
                },
              ]
            : []),
          ...(catalog.corporateShare !== undefined
            ? [
                {
                  title: `${t.H8Q4(companysShareOfMonthlyFeeMsg)} (${t.S8TX(alvZeroMsg)})`,
                  description: getCorporateShareAsText(catalog.corporateShare),
                },
              ]
            : []),
          {
            title: t.QSXP(damageInsuranceMsg),
            description:
              catalog.damageInsurance && catalog.damageInsurance.length > 0
                ? getDamageInsuranceDisplayableItems(catalog.damageInsurance)
                : t.MUF5(notInUseMsg),
          },
          {
            title: t.Q03S('Device enrollment program'),
            description: catalog.enrollmentProgramConsent
              ? `${t.V34H(inUseMsg)}, alias: ${catalog.enrollmentProgramAlias || '-'}`
              : t.MUF5(notInUseMsg),
          },
          {
            title: t.C8DA(catalogCorporateMessageLabelMsg),
            description: catalog.corporateMessage ? catalog.corporateMessage : t.MUF5(notInUseMsg),
          },
          ...(params.catalog.billingAccountId === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE && params.billingAccount
            ? [
                {
                  title: t.EYPA(defaultBillingAccountMsg),
                  description: <BillingAccountInfo billingAccount={params.billingAccount} />,
                },
              ]
            : []),
          ...(params.catalog.billingAccountId !== CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE && billingAccount
            ? [
                {
                  title: billingAccount ? t.EYPA(defaultBillingAccountMsg) : '',
                  description: billingAccount ? <BillingAccountHeaderInfo billingAccountHeader={billingAccount} /> : '',
                },
              ]
            : []),
          {
            title: t.RXBO('Billing account is selected separately for each order'),
            description: catalog.approverMustSelectBillingAccount ? t.V34H(inUseMsg) : t.MUF5(notInUseMsg),
          },
          {
            title: t.RP0S('Restricted list of billing accounts'),
            description:
              catalog.selectedBillingAccountIds && catalog.selectedBillingAccountIds?.length > 0 ? (
                <>
                  <div>
                    {t.EG5X('Selected billing accounts')} ({catalog.selectedBillingAccountIds?.length + 1 || 1}{' '}
                    {t.B3MG(pcsMsg)}):
                  </div>
                  <div className="of-catalog-configuration__virtual-catalog-summary">
                    <Table
                      tableType="bordered"
                      columns={columns}
                      noItemsText={t.NVGB('No billing accounts')}
                      rows={rows || []}
                      showHeaders={false}
                    />
                  </div>
                </>
              ) : (
                t.MUF5(notInUseMsg)
              ),
          },
        ]}
      />
      <CatalogProductsSummary products={params.catalogProducts!} />

      <div className={dsClass.PADDING_VERTICAL_2}>
        <WizardActions
          onBackClick={() => {
            navigate(paths.COMPANY_INFO_CATALOG_LIST_PRODUCTS + search, { state });
          }}
          onForwardClick={() => submitCatalog(params, true)}
          forwardButtonText={t.JC06('publish')}
          backButtonText={t.D1E9(returnMsg)}
          submitting={loading === 'publish'}
        />
      </div>

      <div className={`${dsClass.TEXT_ALIGN_RIGHT} ${dsClass.PADDING_VERTICAL_2}`}>
        <CL.Button
          size="l"
          color="light"
          loading={loading === 'draft'}
          disabled={loading === 'publish'}
          onClick={() => submitCatalog(params, false)}
        >
          {t.TJKD('Save draft')}
        </CL.Button>
        <p className={dsClass.TEXT_S}>
          {t.K6XH('You can save the list as a draft and publish it later.')}
          <br />{' '}
          {t.BZWY('Please note that if you have added a new billing agreement, it will be available immediately.')}
        </p>
      </div>
    </div>
  );
};
